import { useState, useRef, useCallback, useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import { SystemTaskTypes, TaskStatusesBackend } from "@/modules/tasks/enums";
import { AddTaskRequest, TasksResponse, Task } from "@/modules/tasks/dtos";
import { useAddTaskMutation } from "@/modules/tasks/hooks/add-task/use-add-task-mutation.hook";
import useUpdateTask from "@/modules/tasks/hooks/use-update-task.hook";
import { MarkAsCompletedRef } from "@/modules/meetings/components/meetings/mark-as-completed";
import {
  TOAST_IDS,
  ToastType,
  PermissionsEnum,
  PermissionLevel,
} from "@/modules/common/types";
import { showToast, userHasPermission } from "@/modules/common/utils";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";

interface UseMeetingCompletionOptions {
  onSuccess?: () => void;
  onError?: () => void;
  queryKeys?: string[];
  successMessage?: string;
}

export const useMeetingCompletion = (options?: UseMeetingCompletionOptions) => {
  const {
    onSuccess,
    onError,
    queryKeys = ["tasks-dashboard", "task-statistics", "tasks-count"],
    successMessage = "Task completed successfully",
  } = options || {};

  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  // Check if user has permission to disable AI evaluation
  const canSkipEvaluation = userHasPermission(
    {
      name: PermissionsEnum.DISABLE_AI_EVALUATION,
      level: PermissionLevel.READ,
    },
    currentUserPermissions
  );

  const [selectedTask, setSelectedTask] = useState<TasksResponse | Task | null>(
    null
  );
  const [meetingTasks, setMeetingTasks] = useState<AddTaskRequest[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasIncompleteTasks, setHasIncompleteTasks] = useState(false);
  const [tasksReadyForEvaluation, setTasksReadyForEvaluation] = useState(false);
  const [confirmButtonText, setConfirmButtonText] = useState(
    canSkipEvaluation ? "Complete" : "Evaluate"
  );
  const [isEvaluating, setIsEvaluating] = useState(false);
  const markAsCompletedRef = useRef<MarkAsCompletedRef>(null);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedTask(null);
    setMeetingTasks([]);
    setHasIncompleteTasks(false);
    setTasksReadyForEvaluation(false);
    setConfirmButtonText(canSkipEvaluation ? "Complete" : "Evaluate");
    setIsEvaluating(false);
  }, [canSkipEvaluation]);

  const updateTaskMutation = useUpdateTask({
    queryKeys,
    successMessage,
    onSuccess: () => {
      handleCloseModal();
      onSuccess?.();
    },
    onError: () => {
      handleCloseModal();
      onError?.();
    },
  });

  const { mutationAddTask } = useAddTaskMutation({
    setIsSubmittingTask: () => {},
    resetFormState: () => {},
    setOpen: () => {},
  });

  const createTasksMutation = useMutation({
    mutationFn: async (tasks: AddTaskRequest[]) => {
      const createPromises = tasks.map((task) =>
        mutationAddTask.mutateAsync({
          title: task.title,
          type: task.type,
          userUId: task.userUId,
          dueDate: task.dueDate,
          taskUId: task.taskUId,
          linkedEntities: task.linkedEntities || [],
          creationSource: "CRM",
        })
      );
      return Promise.all(createPromises);
    },
    onSuccess: () => {
      handleCloseModal();
      onSuccess?.();
    },
    onError: () => {
      handleCloseModal();
      onError?.();
    },
  });

  const handleConfirmModal = useCallback(async () => {
    if (!selectedTask) return;

    // If user has permission to skip evaluation, or button says "Complete", complete the task
    if (canSkipEvaluation || confirmButtonText === "Complete") {
      try {
        if (meetingTasks.length > 0) {
          await createTasksMutation.mutateAsync(meetingTasks);
        }

        const taskId =
          (selectedTask as any).uid || (selectedTask as any).id?.toString();
        updateTaskMutation.mutate({
          id: taskId,
          status: TaskStatusesBackend.completed,
        });
      } catch (error) {
        console.error("Error completing meeting:", error);
        const taskId =
          (selectedTask as any).uid || (selectedTask as any).id?.toString();
        updateTaskMutation.mutate({
          id: taskId,
          status: TaskStatusesBackend.completed,
        });
      }
    } else if (confirmButtonText === "Evaluate") {
      // Users without permission must evaluate first
      setIsEvaluating(true);
      try {
        const allTasksValid =
          await markAsCompletedRef.current?.evaluateAllTasks();
        if (allTasksValid) {
          setConfirmButtonText("Complete");
        }
      } catch {
        showToast({
          type: ToastType.ERROR,
          message: "Failed to evaluate tasks. Please try again.",
          toastId: TOAST_IDS.TASK_EVALUATION_ERROR,
        });
      } finally {
        setIsEvaluating(false);
      }
    }
  }, [
    selectedTask,
    confirmButtonText,
    meetingTasks,
    createTasksMutation,
    updateTaskMutation,
    canSkipEvaluation,
  ]);

  const handleValidationChange = useCallback((hasIncomplete: boolean) => {
    setHasIncompleteTasks(hasIncomplete);
  }, []);

  const handleTasksNeedEvaluation = useCallback(() => {
    // Only set to "Evaluate" if user doesn't have permission to skip
    if (!canSkipEvaluation) {
      setConfirmButtonText("Evaluate");
    }
  }, [canSkipEvaluation]);

  const handleTasksReadyForEvaluation = useCallback((ready: boolean) => {
    setTasksReadyForEvaluation(ready);
  }, []);

  const openModalForTask = useCallback((task: TasksResponse | Task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  }, []);

  const isMeetingTask = useCallback((task: TasksResponse | Task | null) => {
    return task?.type === SystemTaskTypes.Meeting_subordinate;
  }, []);

  return {
    // State
    state: {
      selectedTask,
      isModalOpen,
      confirmButtonText,
      hasIncompleteTasks,
      tasksReadyForEvaluation,
      isEvaluating,
      isLoading: updateTaskMutation.isPending || isEvaluating,
      markAsCompletedRef,
    },

    handlers: {
      openModalForTask,
      handleCloseModal,
      handleConfirmModal,
      handleValidationChange,
      handleTasksNeedEvaluation,
      handleTasksReadyForEvaluation,
      setMeetingTasks,
    },
    utilities: {
      isMeetingTask,
    },
  };
};
